import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "../components/razor-reuse";
import axios from "../utils/axios";
import {
  buildComparisonParams,
  fetchComparisonCurrencyPairOptions,
  fetchRateComparison,
  fetchRateHistory,
} from "../pages/dashboard/rate-intelligence/rateComparisonApi";
import {
  createRateAlert,
  deleteRateAlert,
  fetchRateAlerts,
  setRateAlertActive,
  updateRateAlert,
} from "../pages/dashboard/rate-intelligence/rateAlertsApi";
import RootState, {
  GetRateAlertsParams,
  GetRateComparisonParams,
  GetRateHistoryParams,
  RateAlertPayload,
  RateIntelligenceState,
  UpdateRateAlertPayload,
} from "./type";

// Rate comparison, history, and rate alerts are all wired to live endpoints
// (see rateComparisonApi.ts / rateAlertsApi.ts) — history shares the exact
// same comparison endpoint, since the API has no separate historical-period
// data. Every thunk reads the result the exact same way (`data?.data?.status`,
// `data?.data?.data`) regardless of which one it calls.

// Filtering requires walking multiple sequential page requests
// (fetchAllComparisonPages in rateComparisonApi.ts) — if one of those pages
// fails partway through, surface it clearly instead of silently rendering
// "no matches" for a fetch that didn't actually finish.
function notifyRateFetchFailure(error: any) {
  if (!error?.response) {
    toast.error(
      "You appear to be offline. Please check your connection and try again.",
      { position: "top-right" },
    );
  } else {
    toast.error("Failed to load rates. Please try again.", { position: "top-right" });
  }
}

export const getRateComparison = createAsyncThunk(
  "web/get-rate-comparison",
  async (payload: GetRateComparisonParams, thunkAPI) => {
    try {
      const data = await fetchRateComparison(payload);
      if (data?.data?.status) {
        thunkAPI.dispatch(SET_COMPARISON(data?.data?.data));
        return data;
      }
      return data;
    } catch (error: any) {
      notifyRateFetchFailure(error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);

// Populates the Comparison tab's "Currency pair" filter dropdown from the
// full live dataset (separate from the filtered/paginated fetch above, which
// only ever holds the current page's subset).
export const getComparisonCurrencyPairOptions = createAsyncThunk(
  "web/get-rate-comparison-currency-pair-options",
  async (_payload: void, thunkAPI) => {
    try {
      const data = await fetchComparisonCurrencyPairOptions();
      if (data?.data?.status) {
        thunkAPI.dispatch(SET_COMPARISON_CURRENCY_PAIR_OPTIONS(data?.data?.data?.options));
        return data;
      }
      return data;
    } catch (error: any) {
      notifyRateFetchFailure(error);
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    // Building this list means walking every page of the comparison
    // endpoint (10/page), so Comparison, History, and Alerts all dispatch
    // this on mount — without this guard, every tab visit would re-walk
    // the full page set again. Skip once it's already populated.
    condition: (_payload, { getState }) => {
      const state = getState() as RootState;
      return !state.rate_intelligence.comparison_currency_pair_options?.length;
    },
  },
);

export const getRateHistory = createAsyncThunk(
  "web/get-rate-history",
  async (payload: GetRateHistoryParams, thunkAPI) => {
    try {
      const data = await fetchRateHistory(payload);
      if (data?.data?.status) {
        thunkAPI.dispatch(SET_HISTORY(data?.data?.data));
        return data;
      }
      return data;
    } catch (error: any) {
      notifyRateFetchFailure(error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);

// CSV export — follows the same convention used everywhere else in this app
// (customer.ts/transaction.ts/report.ts): `?export=csv` on the same list
// endpoint, read back as a Blob so a real file and a JSON error response can
// be told apart by content-type, same as transaction.ts's
// getZojapayTransactionCsv/getBridgeTransactionCsv. Comparison and History
// share the exact same underlying endpoint, so one thunk covers both tabs.
const EXPORT_EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  "text/csv": "csv",
  "application/csv": "csv",
};

function getExportExtension(contentType: string | undefined, fallback: string) {
  const normalized = (contentType || "").split(";")[0].trim().toLowerCase();
  return EXPORT_EXTENSION_BY_CONTENT_TYPE[normalized] || fallback;
}

async function tryParseBlobAsJson(blob: Blob): Promise<any | null> {
  try {
    const text = await blob.text();
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

export const exportRateComparisonCsv = createAsyncThunk(
  "web/export-rate-comparison-csv",
  async (
    payload: GetRateHistoryParams & { fileLabel: string },
    thunkAPI,
  ) => {
    try {
      toast.info("Download in progress...", { position: "top-right" });

      // Reuses the exact same param-building logic as the table's own data
      // fetch (fetchRateComparison/fetchRateHistory) so the export always
      // reflects whatever currency pair / position / period filter is
      // currently applied in the UI.
      const res = await axios.get("/admin/exchange-rates/comparison", {
        params: { ...buildComparisonParams(payload), export: "csv" },
        responseType: "blob",
      });

      // The shared axios instance's response interceptor resolves instead
      // of rejecting on most errors, so a failed request can land here as
      // an AxiosError rather than throwing.
      if ((res as any)?.isAxiosError) throw res;

      const contentType = res?.headers?.["content-type"] as string | undefined;
      const isJsonResponse = !!contentType?.includes("application/json");

      if (isJsonResponse) {
        const json = await tryParseBlobAsJson(res.data as Blob);
        if (json?.status === false || json?.status === "fail") {
          toast.error(json?.message || "Export failed", { position: "top-right" });
          return thunkAPI.rejectWithValue(json);
        }
      }

      if (res?.status === 200 && !isJsonResponse) {
        const extension = getExportExtension(contentType, "csv");
        const url = window.URL.createObjectURL(res.data as Blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${payload.fileLabel}.${extension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      return res;
    } catch (error: any) {
      if (!error?.response) {
        toast.error(
          "You appear to be offline. Please check your connection and try again.",
          { position: "top-right" },
        );
        return thunkAPI.rejectWithValue(error);
      }

      // Error responses also arrive as a Blob when responseType is "blob".
      const errorBlob = error?.response?.data;
      if (errorBlob instanceof Blob) {
        const json = await tryParseBlobAsJson(errorBlob);
        if (json?.message) {
          toast.error(json.message, { position: "top-right" });
        }
      }
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getRateAlerts = createAsyncThunk(
  "web/get-rate-alerts",
  async (payload: GetRateAlertsParams, thunkAPI) => {
    try {
      const data = await fetchRateAlerts(payload);
      if (data?.data?.status) {
        thunkAPI.dispatch(SET_ALERTS(data?.data?.data));
        return data;
      }
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

// Mutating alert thunks (create/update/activate/deactivate/delete) don't
// merge a response into local state — the real response shape for a
// rate-alert resource isn't confirmed yet (see rateAlertsApi.ts) — callers
// re-fetch the current alerts page after a successful mutation instead.

export const createNewRateAlert = createAsyncThunk(
  "web/create-rate-alert",
  async (payload: RateAlertPayload, thunkAPI) => {
    try {
      return await createRateAlert(payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateExistingRateAlert = createAsyncThunk(
  "web/update-rate-alert",
  async (payload: UpdateRateAlertPayload, thunkAPI) => {
    try {
      return await updateRateAlert(payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const setRateAlertActiveStatus = createAsyncThunk(
  "web/set-rate-alert-active-status",
  async (payload: { id: string; active: boolean }, thunkAPI) => {
    try {
      return await setRateAlertActive(payload.id, payload.active);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const deleteExistingRateAlert = createAsyncThunk(
  "web/delete-rate-alert",
  async (id: string, thunkAPI) => {
    try {
      return await deleteRateAlert(id);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const initialState: RateIntelligenceState = {
  loading_comparison: false,
  comparison_pairs: undefined,
  comparison_quoted_at: undefined,
  comparison_meta: undefined,
  comparison_currency_pair_options: undefined,
  loading_history: false,
  history_pairs: undefined,
  history_quoted_at: undefined,
  history_meta: undefined,
  loading_alerts: false,
  alert_rules: undefined,
  alerts_meta: undefined,
  loading_create_alert: false,
  loading_update_alert: false,
  loading_toggle_alert: false,
  loading_delete_alert: false,
  loading_export: false,
};

export const rateIntelligenceSlice = createSlice({
  name: "rate_intelligence",
  initialState,
  reducers: {
    SET_COMPARISON: (state, action) => {
      state.comparison_pairs = action.payload?.pairs || [];
      state.comparison_quoted_at = action.payload?.quoted_at;
      state.comparison_meta = action.payload?.meta;
    },
    SET_COMPARISON_CURRENCY_PAIR_OPTIONS: (state, action) => {
      state.comparison_currency_pair_options = action.payload || [];
    },
    SET_HISTORY: (state, action) => {
      state.history_pairs = action.payload?.pairs || [];
      state.history_quoted_at = action.payload?.quoted_at;
      state.history_meta = action.payload?.meta;
    },
    SET_ALERTS: (state, action) => {
      state.alert_rules = action.payload?.rules || [];
      state.alerts_meta = action.payload?.meta;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRateComparison.pending, (state) => {
      state.loading_comparison = true;
    });
    builder.addCase(getRateComparison.fulfilled, (state) => {
      state.loading_comparison = false;
    });
    builder.addCase(getRateComparison.rejected, (state) => {
      state.loading_comparison = false;
    });
    builder.addCase(getRateHistory.pending, (state) => {
      state.loading_history = true;
    });
    builder.addCase(getRateHistory.fulfilled, (state) => {
      state.loading_history = false;
    });
    builder.addCase(getRateHistory.rejected, (state) => {
      state.loading_history = false;
    });
    builder.addCase(getRateAlerts.pending, (state) => {
      state.loading_alerts = true;
    });
    builder.addCase(getRateAlerts.fulfilled, (state) => {
      state.loading_alerts = false;
    });
    builder.addCase(getRateAlerts.rejected, (state) => {
      state.loading_alerts = false;
    });
    builder.addCase(createNewRateAlert.pending, (state) => {
      state.loading_create_alert = true;
    });
    builder.addCase(createNewRateAlert.fulfilled, (state) => {
      state.loading_create_alert = false;
    });
    builder.addCase(createNewRateAlert.rejected, (state) => {
      state.loading_create_alert = false;
    });
    builder.addCase(updateExistingRateAlert.pending, (state) => {
      state.loading_update_alert = true;
    });
    builder.addCase(updateExistingRateAlert.fulfilled, (state) => {
      state.loading_update_alert = false;
    });
    builder.addCase(updateExistingRateAlert.rejected, (state) => {
      state.loading_update_alert = false;
    });
    builder.addCase(setRateAlertActiveStatus.pending, (state) => {
      state.loading_toggle_alert = true;
    });
    builder.addCase(setRateAlertActiveStatus.fulfilled, (state) => {
      state.loading_toggle_alert = false;
    });
    builder.addCase(setRateAlertActiveStatus.rejected, (state) => {
      state.loading_toggle_alert = false;
    });
    builder.addCase(deleteExistingRateAlert.pending, (state) => {
      state.loading_delete_alert = true;
    });
    builder.addCase(deleteExistingRateAlert.fulfilled, (state) => {
      state.loading_delete_alert = false;
    });
    builder.addCase(deleteExistingRateAlert.rejected, (state) => {
      state.loading_delete_alert = false;
    });
    builder.addCase(exportRateComparisonCsv.pending, (state) => {
      state.loading_export = true;
    });
    builder.addCase(exportRateComparisonCsv.fulfilled, (state) => {
      state.loading_export = false;
    });
    builder.addCase(exportRateComparisonCsv.rejected, (state) => {
      state.loading_export = false;
    });
  },
});

export const {
  SET_COMPARISON,
  SET_COMPARISON_CURRENCY_PAIR_OPTIONS,
  SET_HISTORY,
  SET_ALERTS,
} = rateIntelligenceSlice.actions;

export default rateIntelligenceSlice.reducer;
